import React, { useEffect, useMemo } from 'react';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import {
	whoCanAccessSpaceWhatRolesPrompt,
	whyUserCannotViewEditWorkItemsPrompt,
	whoHasSpaceAdminOrManageSprintsPermissionsPrompt,
} from '@jira/ai-rovo__actions-permissions/src/PermissionsConfigProviderEntry.tsx';
import { useExplicitPermissionsSkillPrompt } from '@jira/ai-rovo__actions-permissions/src/useExplicitPermissionsSkillPrompt.tsx';
import { useAiRovoCurrentRouteConfigurationStoreActions } from '@jira/ai-rovo_actions-store/src/aiRovoCurrentRouteConfigurationStore.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	HandleActionType,
	type ConversationAction,
} from '@atlassian/rovo-conversation-actions/types';
import { useRandomNudge } from './useRandomNudge.tsx';

export const AiRovoPermissionsNudgesProvider: React.FC<{}> = (): null => {
	const { formatMessage } = useIntl();
	const { updateConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();
	const { formatExplicitPermissionsSkillPrompt } = useExplicitPermissionsSkillPrompt();

	const nudges = useMemo<ConversationAction[]>(
		() => [
			{
				id: 'ai-rovo.permissions-nudge.who-can-access-space-what-roles',
				title: formatMessage(whoCanAccessSpaceWhatRolesPrompt),
				description: formatMessage(whoCanAccessSpaceWhatRolesPrompt),
				icon: () => (
					<AiChatIcon label={formatMessage(whoCanAccessSpaceWhatRolesPrompt)} size="medium" />
				),
				type: 'skill-action',
				handleType: HandleActionType.Skill,
				staticPrompt: formatExplicitPermissionsSkillPrompt(whoCanAccessSpaceWhatRolesPrompt),
			},
			{
				id: 'ai-rovo.permissions-nudge.why-user-cannot-view-edit-work-items',
				title: formatMessage(whyUserCannotViewEditWorkItemsPrompt),
				description: formatMessage(whyUserCannotViewEditWorkItemsPrompt),
				icon: () => (
					<AiChatIcon label={formatMessage(whyUserCannotViewEditWorkItemsPrompt)} size="medium" />
				),
				type: 'skill-action',
				handleType: HandleActionType.Skill,
				staticPrompt: formatExplicitPermissionsSkillPrompt(whyUserCannotViewEditWorkItemsPrompt),
			},
			{
				id: 'ai-rovo.permissions-nudge.who-has-space-admin-sprint-management-permissions',
				title: formatMessage(whoHasSpaceAdminOrManageSprintsPermissionsPrompt),
				description: formatMessage(whoHasSpaceAdminOrManageSprintsPermissionsPrompt),
				icon: () => (
					<AiChatIcon
						label={formatMessage(whoHasSpaceAdminOrManageSprintsPermissionsPrompt)}
						size="medium"
					/>
				),
				type: 'skill-action',
				handleType: HandleActionType.Skill,
				staticPrompt: formatExplicitPermissionsSkillPrompt(
					whoHasSpaceAdminOrManageSprintsPermissionsPrompt,
				),
			},
		],
		[formatMessage, formatExplicitPermissionsSkillPrompt],
	);

	const randomNudge = useRandomNudge(nudges);

	useEffect(() => {
		if (randomNudge) {
			updateConfiguration({ nudges: [randomNudge] });
		}
	}, [randomNudge, updateConfiguration]);

	return null;
};
